import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Code2, Copy, ExternalLink, Eye, MousePointerClick, LayoutGrid } from 'lucide-react';

/**
 * Generates a drop-in embed snippet partners can paste on their site.
 * Supports two modes:
 *   - inline (default) — renders the booking widget where the snippet is
 *   - button (modal) — renders a Call-To-Action button that opens the
 *                       widget in a popover when clicked
 *
 * Also shows a live preview using the same widget.js script the partner
 * will be running, so what they see here is exactly what they'll ship.
 */
export default function EmbedCodeGenerator({ event }) {
  const [mode, setMode] = useState('inline');     // inline | button
  const [accent, setAccent] = useState('');       // optional brand hex
  const [theme, setTheme] = useState('');         // light | dark | (auto)
  const [label, setLabel] = useState(
    event?.kind === 'reservation' ? 'Reserve a table' : 'Book now'
  );

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://eventism.com';

  const snippet = useMemo(() => {
    if (!event?.event_id) return '';
    const dataAttrs = [
      `data-eventism-event="${event.event_id}"`,
      `data-kind="${event.kind || 'experience'}"`,
    ];
    if (mode === 'button') {
      dataAttrs.push('data-mode="button"');
      if (label) dataAttrs.push(`data-label="${label.replace(/"/g, '&quot;')}"`);
    }
    if (accent) dataAttrs.push(`data-accent="${accent}"`);
    if (theme)  dataAttrs.push(`data-theme="${theme}"`);
    return (
      `<!-- Eventism ${event.kind || 'booking'} widget -->\n`
      + `<div ${dataAttrs.join(' ')}></div>\n`
      + `<script src="${origin}/widget.js" async></script>`
    );
  }, [event, mode, accent, theme, label, origin]);

  const embedDirectUrl = useMemo(() => {
    if (!event?.event_id) return '';
    const prefix = event.kind === 'reservation' ? 'res' : 'ex';
    const qs = new URLSearchParams();
    if (accent) qs.set('accent', accent);
    if (theme)  qs.set('theme', theme);
    const q = qs.toString();
    return `${origin}/embed/${prefix}/${event.event_id}${q ? `?${q}` : ''}`;
  }, [event, accent, theme, origin]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      toast.success('Snippet copied — paste it into your site');
    } catch {
      toast.error('Could not copy. Select the code and copy manually.');
    }
  };

  if (!event?.event_id) {
    return (
      <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm" data-testid="embed-needs-save">
        <p className="font-semibold">Save the listing first</p>
        <p className="text-xs text-muted-foreground">The embed snippet uses the listing's ID — save the basics and come back here to grab the code.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="embed-code-generator">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Code2 className="h-4 w-4 text-indigo-400" />
          <Label className="text-sm font-semibold">Embed on any website</Label>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Paste the snippet below into your own site, Wix, Webflow, Squarespace, or WordPress. The widget loads in an iframe and auto-resizes — no CSS conflicts.
        </p>
      </div>

      {/* Mode selector */}
      <div className="grid sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setMode('inline')}
          className={`text-left p-3 rounded-lg border transition ${mode === 'inline' ? 'border-indigo-500 ring-2 ring-indigo-500/40 bg-indigo-500/5' : 'border-border hover:bg-muted/40'}`}
          data-testid="embed-mode-inline"
        >
          <div className="flex items-center gap-2 mb-1"><LayoutGrid className="h-3.5 w-3.5" /><span className="font-semibold text-sm">Inline</span></div>
          <p className="text-[11px] text-muted-foreground">Renders the full booking form right where the snippet is placed.</p>
        </button>
        <button
          type="button"
          onClick={() => setMode('button')}
          className={`text-left p-3 rounded-lg border transition ${mode === 'button' ? 'border-indigo-500 ring-2 ring-indigo-500/40 bg-indigo-500/5' : 'border-border hover:bg-muted/40'}`}
          data-testid="embed-mode-button"
        >
          <div className="flex items-center gap-2 mb-1"><MousePointerClick className="h-3.5 w-3.5" /><span className="font-semibold text-sm">Button → modal</span></div>
          <p className="text-[11px] text-muted-foreground">Renders a call-to-action button; opens the widget in a popover on click.</p>
        </button>
      </div>

      {/* Customizers */}
      <div className="grid sm:grid-cols-3 gap-3">
        {mode === 'button' && (
          <div className="space-y-1.5 sm:col-span-3">
            <Label className="text-xs">Button label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} data-testid="embed-label" />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs">Accent color (optional)</Label>
          <Input value={accent} onChange={(e) => setAccent(e.target.value)} placeholder="#FF6B00" data-testid="embed-accent" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Theme</Label>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            className="h-10 px-3 border rounded-md bg-background text-sm w-full"
            data-testid="embed-theme"
          >
            <option value="">Match parent site</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Direct embed URL</Label>
          <a
            href={embedDirectUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="h-10 px-3 inline-flex items-center gap-1 border rounded-md bg-background text-xs text-indigo-300 hover:text-indigo-200 truncate"
            data-testid="embed-direct-url"
          >
            Open <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* Snippet */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">Copy &amp; paste</Label>
          <Button type="button" size="sm" onClick={copy} data-testid="embed-copy-btn">
            <Copy className="h-3 w-3 mr-1" /> Copy
          </Button>
        </div>
        <pre
          className="text-[11px] leading-relaxed font-mono p-3 rounded-lg border bg-muted/40 overflow-auto whitespace-pre-wrap break-all"
          data-testid="embed-snippet"
        >{snippet}</pre>
      </div>

      {/* Live preview */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Eye className="h-3 w-3" />
          <span>Live preview (the exact iframe partners will see)</span>
        </div>
        <div className="rounded-xl border bg-background overflow-hidden" data-testid="embed-preview-wrap">
          <iframe
            key={embedDirectUrl}
            src={embedDirectUrl}
            title="Embed preview"
            loading="lazy"
            className="w-full"
            style={{ border: 0, minHeight: 560 }}
            data-testid="embed-preview-iframe"
          />
        </div>
      </div>
    </div>
  );
}
